import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _extends from "@babel/runtime/helpers/extends";

function _createSuper(Derived) {
  function isNativeReflectConstruct() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;

    try {
      Date.prototype.toString.call(Reflect.construct(Date, [], function () {}));
      return true;
    } catch (e) {
      return false;
    }
  }

  return function () {
    var Super = _getPrototypeOf(Derived),
        result;

    if (isNativeReflectConstruct()) {
      var NewTarget = _getPrototypeOf(this).constructor;

      result = Reflect.construct(Super, arguments, NewTarget);
    } else {
      result = Super.apply(this, arguments);
    }

    return _possibleConstructorReturn(this, result);
  };
}

import React, { PureComponent, Children, createElement, cloneElement } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import throttle from 'lodash.throttle';
import Menu from '../Menu/Menu';
import Input from '../Input';
import { TagsInput, TagsInputItem } from '../TagsInput';
import Dropdown from '../Dropdown';
import OnClickOutside from '../OnClickOutside';
import { TAB, UP, DOWN, ESCAPE, BACKSPACE, DELETE, ENTER } from '../constants/keys';
import { withStyles } from '../theme';
import { isolateMixin, placeholderMixin, ifMobile } from '../utils/mixins';
import { ios, android } from '../utils/browser';
import ClearIconSmall from './ClearIconSmall';
import ChevronDownCompactIcon from '../icons/forms/ChevronDownCompactIcon';
var isNativeSelectAllowed = ios || android;
var emptyArr = [];

var noop = function noop() {};

var absolutePosition = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0
};
/* Prevent strange behaviour of onChange event in multiple select */

/* http://stackoverflow.com/questions/34660500/mobile-safari-multi-select-bug */

var multipleSelectFix = React.createElement("optgroup", {
  disabled: true,
  hidden: true
});

var assignStatusColors = function assignStatusColors(borderColor) {
  return {
    '& $field': {
      borderColor: borderColor
    }
  };
};

var styles = function styles(theme) {
  var _extends5;

  return _extends({
    root: {
      extend: isolateMixin,
      position: 'relative',
      '&$isDisabled': {
        cursor: 'not-allowed',
        '& $input': {
          pointerEvents: 'none'
        },
        '& $arrow': {
          color: theme.field.colors.disabled.arrow + '!important',
          pointerEvents: 'none'
        }
      },
      '&$error': assignStatusColors(theme.field.colors.error.outline),
      '&$warning': assignStatusColors(theme.field.colors.warning.outline),
      '&$success': assignStatusColors(theme.field.colors.success.outline)
    },
    dropdownContainer: {
      '&&': {
        display: 'block'
      },
      '$isMultipleWithoutSearch &': {
        extend: absolutePosition,
        bottom: null
      }
    },
    icon: {
      '$isMultipleWithoutSearch &': {
        top: 'auto',
        bottom: 'auto',
        margin: 0,
        transform: 'translateY(-50%)'
      },
      zIndex: 1
    },
    arrow: {
      cursor: 'pointer',
      textAlign: 'center',
      lineHeight: 0,
      color: theme.field.colors.default.arrow,
      pointerEvents: 'auto',
      '& svg': {
        extend: absolutePosition,
        margin: 'auto',
        maxWidth: '100%',
        maxHeight: '100%'
      },
      transitionProperty: 'color',
      transitionDuration: 200
    },
    arrowIcon: {
      position: 'absolute',
      transform: 'rotate3d(0, 0, 1, 0deg)',
      transitionProperty: 'transform',
      transitionDuration: 200
    },
    input: {
      '$withCustom &': absolutePosition,
      '$isEnabled$isFocused &': {
        '& $arrow': {
          color: theme.field.colors.focus.arrow
        },
        '&:hover': {
          '& $arrow': {
            color: theme.field.colors.hover.arrow
          }
        }
      },
      '$isEnabled &': {
        '&:hover': {
          '& $field': {
            color: theme.select.colors.hover.text
          },
          '& $arrow': {
            color: theme.field.colors.hover.arrow
          }
        }
      },
      borderRadius: theme.field.borderRadius
    },
    inputBorder: {},
    field: {
      '$isReadonly &': {
        cursor: 'pointer',
        userSelect: 'none'
      },
      '$withSearch &': {
        cursor: 'text'
      },
      '$withCustom &&': {
        extend: absolutePosition,
        height: '100%'
      }
    },
    darkPlaceholder: {
      '$isEnabled$isReadonly &, $isEnabled:not($isFocused) &': _extends({}, placeholderMixin('&', {
        opacity: 1,
        color: theme.select.colors.default.placeholder,
        transitionProperty: 'color',
        transitionDuration: 200
      }), {}, placeholderMixin('&:hover', {
        color: theme.select.colors.hover.placeholder
      })),
      '$isEnabled$isReadonly $icon:hover ~ &, $isEnabled:not($isFocused) $icon:hover ~ &': _extends({}, placeholderMixin('&', {
        color: theme.select.colors.hover.placeholder
      }))
    },
    withCustom: {
      position: 'relative',
      '$isOpened &': {
        zIndex: 1
      }
    },
    withTopDropdown: {
      '& + *': {
        '& $dropdown': {
          marginBottom: theme.select.dropdown.marginTop
        }
      },
      '$regular$isOpened & + *': {
        '& $menu': {
          borderBottomLeftRadius: theme.dropdown.borderRadius,
          borderBottomRightRadius: theme.dropdown.borderRadius
        }
      }
    },
    withBottomDropdown: {
      '& + *': {
        '& $dropdown': {
          marginTop: theme.select.dropdown.marginTop
        }
      },
      '$regular$isOpened & + *': {
        '& $menu': {
          borderBottomLeftRadius: theme.dropdown.borderRadius,
          borderBottomRightRadius: theme.dropdown.borderRadius
        }
      }
    },
    custom: {
      position: 'relative',
      pointerEvents: 'none'
    },
    multipleValueItem: {
      '$isEnabled $options-regular &': {
        color: theme.field.colors.default.text
      }
    },
    options: {
      composes: '$custom'
    },
    dropdown: {
      '&&': {
        boxShadow: theme.select.dropdown.boxShadow,
        overflow: 'hidden',
        border: "".concat(theme.select.dropdown.borderWidth, "px solid ").concat(theme.menu.colors.default.border),
        borderBottom: 0,
        backgroundColor: theme.menu.colors.default.background,
        '&$isMultipleDropdown, $regular &': {
          transitionProperty: 'opacity',
          top: '0 !important'
        }
      }
    },
    selected: {
      borderBottom: "".concat(theme.select.dropdown.borderWidth, "px solid ").concat(theme.menu.colors.default.border),
      cursor: 'default'
    },
    menu: _extends({
      borderBottom: "".concat(theme.select.dropdown.borderWidth, "px solid ").concat(theme.menu.colors.default.border),
      boxSizing: 'content-box'
    }, theme.select.dropdown.borderWidth === 0 && {
      '&:before, &:after': {
        content: '""',
        position: 'absolute',
        left: 0,
        width: '100%',
        height: Math.max(5, theme.dropdown.borderRadius),
        opacity: 0,
        pointerEvents: 'none',
        zIndex: 1
      },
      '&:before': {
        top: 0,
        borderTopLeftRadius: theme.dropdown.borderRadius,
        borderTopRightRadius: theme.dropdown.borderRadius,
        background: "linear-gradient(to bottom, ".concat(theme.field.colors.default.background, " 0 5px, transparent 5px 100%)")
      },
      '&:after': {
        bottom: 0,
        borderBottomLeftRadius: theme.dropdown.borderRadius,
        borderBottomRightRadius: theme.dropdown.borderRadius,
        background: "linear-gradient(to top, ".concat(theme.field.colors.default.background, " 0 5px, transparent 5px 100%)")
      }
    }),
    'menuOverflowY-top': {
      '&:before': {
        opacity: 1
      }
    },
    'menuOverflowY-bottom': {
      '&:after': {
        opacity: 1
      }
    },
    'menuOverflowY-both': {
      composes: ['$menuOverflowY-top', '$menuOverflowY-bottom']
    },
    menuItem: {
      backgroundColor: theme.menu.colors.default.background
    }
  }, ['small', 'medium'].reduce(function (result, size) {
    return _extends({}, result, _defineProperty({}, "menuSize-".concat(size), {
      maxHeight: theme.menu.sizes[size].height * 7 + 2,
      '&$reducedHeight': {
        maxHeight: theme.menu.sizes[size].height * 6 + 2
      }
    }));
  }, {}), {
    reducedHeight: {},
    clear: {
      flex: 'none',
      alignSelf: 'center',
      color: theme.field.icon.colors.default,
      fill: 'currentColor',
      cursor: 'pointer',
      pointerEvents: 'auto',
      '&:hover , &:active': {
        color: theme.field.icon.colors.active
      }
    }
  }, ['medium', 'small'].reduce(function (result, size) {
    return _extends({}, result, _defineProperty({}, size, _extends({
      '&$isMultipleWithoutSearch': {
        height: theme.field.sizes[size].height
      },
      '&$isMultipleWithoutSearch $icon': {
        top: theme.field.sizes[size].height / 2
      },
      '& $withCustom': {
        minHeight: theme.field.sizes[size].height
      },
      '& $arrow': {
        '&:before': _extends({
          display: 'block',
          content: '" "',
          position: 'absolute',
          top: -Math.floor((theme.field.sizes[size].height - theme.field.sizes[size].icon) / 2),
          bottom: -Math.floor((theme.field.sizes[size].height - theme.field.sizes[size].icon) / 2),
          left: -10,
          right: -10
        }, ifMobile({
          top: -Math.floor((theme.field.sizes[size].height - theme.field.mobile.sizes[size].icon) / 2),
          bottom: -Math.floor((theme.field.sizes[size].height - theme.field.mobile.sizes[size].icon) / 2)
        }), {
          borderRadius: theme.field.borderRadius
        })
      },
      '&$isOpened $arrowIcon': {
        transform: 'rotate3d(0, 0, 1, 180deg)'
      },
      '& $custom': {
        paddingRight: theme.input.sizes[size].padding + 1,
        paddingLeft: theme.input.sizes[size].padding + 1
      },
      '&$withLeftIcon $custom': {
        paddingLeft: theme.field.sizes[size].withIconPadding + 1
      },
      '&$withRightIcon $custom': {
        paddingRight: theme.field.sizes[size].withIconPadding + 1
      }
    }, ['regular', 'background'].reduce(function (result, type) {
      var _extends3;

      var verticalPadding = (theme.field.sizes[size].height - theme.tagsInput.types[type].height) / 2;
      return _extends({}, result, (_extends3 = {}, _defineProperty(_extends3, "& $options$options-".concat(type), {
        paddingTop: verticalPadding,
        paddingBottom: verticalPadding
      }), _defineProperty(_extends3, "& $selected$options-".concat(type), {
        padding: "".concat(verticalPadding - 1, "px ").concat(theme.input.sizes[size].padding - 1, "px")
      }), _extends3));
    }, {}))));
  }, {}), (_extends5 = {
    isNative: {
      '& $icon': {
        pointerEvents: 'none'
      }
    },
    nativeSelect: {
      extend: [isolateMixin, absolutePosition],
      width: '100%',
      height: '100%',
      opacity: 0,
      overflow: 'hidden',
      outline: 0
    },
    regular: {},
    awesome: {},
    promo: {},
    success: {},
    error: {},
    warning: {},
    isFocused: {},
    isOpened: {
      '& $field': {
        backgroundColor: theme.field.colors.default.background
      }
    },
    isReadonly: {},
    isEnabled: {},
    isDisabled: {},
    isMultipleWithoutSearch: {},
    isMultipleDropdown: {},
    withSearch: {},
    withLeftIcon: {},
    withRightIcon: {}
  }, _defineProperty(_extends5, 'options-regular', {}), _defineProperty(_extends5, 'options-background', {}), _extends5));
};

var Select = /*#__PURE__*/function (_PureComponent) {
  _inherits(Select, _PureComponent);

  var _super = _createSuper(Select);

  function Select() {
    var _this;

    _classCallCheck(this, Select);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));
    _this.state = {
      isOpened: false,
      inputFocused: false,
      searchText: '',
      value: _this.initialValue,
      menuOverflowY: false
    };

    _this.handleDropdownClose = function () {
      if (_this.state.isOpened || _this.props.inputMode) return;

      _this.setSearchText('');
    };

    _this.requestItems = function (event) {
      _this.setState({
        isOpened: true
      });

      _this.setSearchText(event.target.value);
    };

    _this.setMenuOverflow = function (event) {
      var scrollTop = event.scrollTop,
          scrollHeight = event.scrollHeight,
          clientHeight = event.clientHeight;
      var overflowedTop = scrollTop > 0;
      var overflowedBottom = scrollTop < scrollHeight - clientHeight;
      var menuOverflowY = overflowedTop && overflowedBottom ? 'both' : overflowedTop ? 'top' : overflowedBottom ? 'bottom' : false;

      _this.setState({
        menuOverflowY: menuOverflowY
      });
    };

    _this.onMenuScrollMount = function (scroll) {
      _this.menuScroll = scroll;
    };

    _this.onMenuScroll = throttle(_this.setMenuOverflow);

    _this.onDropdownOpen = function () {
      if (_this.menuScroll) _this.setMenuOverflow(_this.menuScroll.getScrollState());
    };

    _this.changeValue = function (value) {
      var _this$props = _this.props,
          multiple = _this$props.multiple,
          inputMode = _this$props.inputMode,
          onChange = _this$props.onChange;
      if (!multiple) _this.setState({
        isOpened: false,
        menuOverflowY: false
      });
      if (inputMode) _this.setSearchText(value || '');
      var nextState = Select.getValueState(value, _this.props, _this.state);
      if (nextState) _this.setState(nextState);
      if (onChange) onChange(value);
      if (!inputMode && !multiple) _this.input.focus();
    };

    _this.focusInput = function (event) {
      var onFocus = _this.props.onFocus;

      _this.setState({
        inputFocused: true
      });

      if (onFocus && !_this.state.isOpened) onFocus(event);
    };

    _this.blurInput = function (event) {
      if (!_this.state.inputFocused) return;
      var _this$props2 = _this.props,
          inputMode = _this$props2.inputMode,
          onBlur = _this$props2.onBlur;
      if (inputMode) _this.changeValue(_this.state.searchText);
      if (onBlur) onBlur(event);
    };

    _this.preventBlurInput = function (event) {
      event.preventDefault();
    };

    _this.preventSelect = function (event) {
      event.preventDefault();
      event.stopPropagation();
    };

    _this.open = function () {
      if (_this.props.disabled || _this.state.isOpened) return;

      _this.setState({
        isOpened: true
      });

      setTimeout(function () {
        _this.input.focus();
      }, 0);
    };

    _this.close = function () {
      if (!_this.state.isOpened) return;

      _this.setState({
        isOpened: false,
        menuOverflowY: false
      });
    };

    _this.openOnArrowClick = function () {
      var isOpened = _this.state.isOpened;
      if (isOpened) _this.close();else _this.open();
    };

    _this.onClear = function () {
      _this.setSearchText('');

      _this.changeValue();
    };

    _this.closeOnEsc = function (event) {
      if (!_this.state.isOpened) return;
      event.stopPropagation();

      _this.setState({
        isOpened: false,
        menuOverflowY: false
      });

      _this.input.focus();
    };

    _this.saveInputRef = function (ref) {
      var inputRef = _this.props.inputRef;
      if (inputRef) inputRef(ref);
      _this.input = ref;
    };

    _this.closeOnClickOutside = function () {
      var isOpened = _this.state.isOpened;
      if (!isOpened) return;

      _this.setState({
        isOpened: false,
        inputFocused: false,
        menuOverflowY: false
      });
    };

    _this.keyDown = function (event) {
      var code = event.keyCode;
      var _this$props3 = _this.props,
          inputMode = _this$props3.inputMode,
          multiple = _this$props3.multiple,
          customElementRenderer = _this$props3.customElementRenderer,
          onKeyDown = _this$props3.onKeyDown;
      if (onKeyDown) onKeyDown(event);
      if (code === ESCAPE) _this.closeOnEsc(event);else if (code === TAB) _this.setState({
        isOpened: false,
        menuOverflowY: false
      });else if (code === UP || code === DOWN) _this.openOnArrowKey(event);else if (inputMode && code === ENTER) _this.changeValue(_this.state.searchText);else if (!multiple && !customElementRenderer && (code === DELETE || code === BACKSPACE)) _this.clearValueOnBackspace(event);
    };

    _this.Arrow = function (props) {
      var isOpened = _this.state.isOpened;
      var _this$props4 = _this.props,
          arrowStyle = _this$props4.arrowStyle,
          arrowClassName = _this$props4.arrowClassName,
          arrowIcon = _this$props4.arrowIcon,
          classes = _this$props4.classes;

      var className = props.className,
          size = props.size,
          color = props.color,
          otherProps = _objectWithoutProperties(props, ["className", "size", "color"]);

      var resultIcon = typeof arrowIcon === 'function' ? arrowIcon({
        isOpened: isOpened
      }) : arrowIcon || React.createElement(ChevronDownCompactIcon, {
        className: classes.arrowIcon,
        size: 20,
        color: "currentColor"
      });
      return React.createElement("div", _extends({
        style: arrowStyle,
        className: classnames(className, classes.arrow, arrowClassName)
      }, otherProps), resultIcon);
    };

    _this.Clear = function () {
      var classes = _this.props.classes;
      return React.createElement(ClearIconSmall, {
        className: classes.clear,
        size: 20,
        color: "currentColor",
        onMouseDown: _this.preventBlurInput,
        onClick: _this.onClear
      });
    };

    _this.handleNativeSelectChange = function (_ref) {
      var target = _ref.target;
      var _this$props5 = _this.props,
          multiple = _this$props5.multiple,
          onChange = _this$props5.onChange;
      var nextValue = multiple ? Array.prototype.map.call(target.selectedOptions, function (item) {
        return _this.values[item.value];
      }) : _this.values[target.value];
      var nextState = Select.getValueState(nextValue, _this.props, _this.state);
      if (nextState) _this.setState(nextState);
      if (onChange) onChange(nextValue);
    };

    return _this;
  }

  _createClass(Select, [{
    key: "setSearchText",
    value: function setSearchText(searchText) {
      if (this.state.searchText === searchText) return;
      this.setState({
        searchText: searchText
      });
      if (this.props.onSearch) this.props.onSearch(searchText);
    }
  }, {
    key: "openOnArrowKey",
    value: function openOnArrowKey(event) {
      var _this2 = this;

      event.preventDefault();
      if (!this.state.isOpened) this.setState({
        isOpened: true
      });
      setTimeout(function () {
        _this2.setState({
          inputFocused: false
        });
      }, 0);
    }
  }, {
    key: "clearValueOnBackspace",
    value: function clearValueOnBackspace() {
      var _this$state = this.state,
          searchText = _this$state.searchText,
          inputFocused = _this$state.inputFocused;
      if (this.props.onSearch && inputFocused && searchText === '') this.changeValue();
    }
  }, {
    key: "isValueEmpty",
    value: function isValueEmpty(value) {
      return value == null || value === '';
    }
  }, {
    key: "getInputProps",
    value: function getInputProps() {
      var _this$props6 = this.props,
          className = _this$props6.className,
          style = _this$props6.style,
          placeholder = _this$props6.placeholder,
          icon = _this$props6.icon,
          onSearch = _this$props6.onSearch,
          dropdownClassName = _this$props6.dropdownClassName,
          dropdownStyle = _this$props6.dropdownStyle,
          menuClassName = _this$props6.menuClassName,
          menuStyle = _this$props6.menuStyle,
          valuesEquality = _this$props6.valuesEquality,
          children = _this$props6.children,
          value = _this$props6.value,
          appendToBody = _this$props6.appendToBody,
          classes = _this$props6.classes,
          onFocus = _this$props6.onFocus,
          onBlur = _this$props6.onBlur,
          onKeyDown = _this$props6.onKeyDown,
          onChange = _this$props6.onChange,
          inputValueRenderer = _this$props6.inputValueRenderer,
          iconElementRenderer = _this$props6.iconElementRenderer,
          customElementRenderer = _this$props6.customElementRenderer,
          multiple = _this$props6.multiple,
          multipleType = _this$props6.multipleType,
          theme = _this$props6.theme,
          arrowClassName = _this$props6.arrowClassName,
          arrowStyle = _this$props6.arrowStyle,
          arrowIcon = _this$props6.arrowIcon,
          containerStyle = _this$props6.containerStyle,
          containerClassName = _this$props6.containerClassName,
          rootStyle = _this$props6.rootStyle,
          rootClassName = _this$props6.rootClassName,
          native = _this$props6.native,
          clearIcon = _this$props6.clearIcon,
          inputMode = _this$props6.inputMode,
          lightPlaceholderColor = _this$props6.lightPlaceholderColor,
          rightIcon = _this$props6.rightIcon,
          inputRef = _this$props6.inputRef,
          props = _objectWithoutProperties(_this$props6, ["className", "style", "placeholder", "icon", "onSearch", "dropdownClassName", "dropdownStyle", "menuClassName", "menuStyle", "valuesEquality", "children", "value", "appendToBody", "classes", "onFocus", "onBlur", "onKeyDown", "onChange", "inputValueRenderer", "iconElementRenderer", "customElementRenderer", "multiple", "multipleType", "theme", "arrowClassName", "arrowStyle", "arrowIcon", "containerStyle", "containerClassName", "rootStyle", "rootClassName", "native", "clearIcon", "inputMode", "lightPlaceholderColor", "rightIcon", "inputRef"]);

      return props;
    }
  }, {
    key: "renderSelectedItems",
    value: function renderSelectedItems() {
      var props = this.props;
      var selected = Array.isArray(props.value) ? props.value : emptyArr;
      return selected.map(function (item) {
        var text = props.inputValueRenderer(item);
        return React.createElement(TagsInputItem, {
          value: item,
          key: text,
          className: props.classes.multipleValueItem,
          icon: props.iconElementRenderer ? props.iconElementRenderer(item) : null
        }, text);
      });
    }
  }, {
    key: "renderInput",
    value: function renderInput() {
      var _this$state2 = this.state,
          value = _this$state2.value,
          searchText = _this$state2.searchText,
          isOpened = _this$state2.isOpened,
          inputFocused = _this$state2.inputFocused;
      var _this$props7 = this.props,
          className = _this$props7.className,
          style = _this$props7.style,
          classes = _this$props7.classes,
          placeholder = _this$props7.placeholder,
          icon = _this$props7.icon,
          readOnly = _this$props7.readOnly,
          onSearch = _this$props7.onSearch,
          multiple = _this$props7.multiple,
          inputValueRenderer = _this$props7.inputValueRenderer,
          customElementRenderer = _this$props7.customElementRenderer,
          inputMode = _this$props7.inputMode,
          lightPlaceholderColor = _this$props7.lightPlaceholderColor,
          rightIcon = _this$props7.rightIcon;
      var focusedInput = inputFocused || isOpened;
      if (inputMode && value != null && typeof value !== 'string') throw new Error('In `inputMode` value of <Select /> should be a string');
      var resultInputValue = '';

      if ((onSearch || inputMode) && focusedInput && isOpened) {
        resultInputValue = searchText;
      } else if (!multiple && !customElementRenderer) {
        var inputValue = inputValueRenderer(value);
        resultInputValue = this.isValueEmpty(inputValue) ? '' : inputValue;
      }

      var resultPlaceholder = '';

      if (customElementRenderer) {
        if (this.isValueEmpty(value) || isOpened && !!onSearch) resultPlaceholder = placeholder;
      } else if (multiple) {
        var withValue = Array.isArray(value) && value.length > 0;
        if (isOpened && onSearch && !readOnly || !withValue) resultPlaceholder = placeholder;
      } else {
        var _inputValue = inputValueRenderer(value);

        resultPlaceholder = this.isValueEmpty(_inputValue) || inputMode && searchText === '' ? placeholder : onSearch && !inputMode && focusedInput && searchText === '' ? _inputValue : '';
      }

      var canBeModified = (inputMode || !!onSearch) && (!customElementRenderer && !multiple || customElementRenderer && (isOpened || this.isValueEmpty(value)) || multiple && (isOpened || !Array.isArray(value) || value.length === 0));
      var resultRightIcon = rightIcon || (this.showClearIcon ? createElement(this.Clear) : this.showArrow && createElement(this.Arrow, {
        role: 'button',
        onMouseDown: this.preventBlurInput,
        onClick: this.openOnArrowClick
      }));
      return React.createElement(Input, _extends({}, this.getInputProps(), {
        inputStyle: style,
        className: classes.input,
        iconLeft: icon,
        iconRight: resultRightIcon,
        iconRightClassName: classes.icon,
        activeBorderClassName: classes.inputBorder,
        onKeyDown: this.keyDown,
        onClick: !onSearch && isOpened ? this.close : this.open,
        onFocus: this.focusInput,
        onBlur: this.blurInput,
        onTouchStart: onSearch ? undefined : this.open,
        onTouchEnd: onSearch ? undefined : this.preventSelect,
        inputClassName: classnames(className, classes.field, !lightPlaceholderColor && classes.darkPlaceholder),
        placeholder: resultPlaceholder,
        readOnly: readOnly || !canBeModified,
        value: resultInputValue,
        onChange: this.requestItems,
        inputRef: this.saveInputRef
      }));
    }
  }, {
    key: "renderSelect",
    value: function renderSelect() {
      var _this3 = this;

      var _this$state3 = this.state,
          value = _this$state3.value,
          inputFocused = _this$state3.inputFocused,
          isOpened = _this$state3.isOpened,
          menuOverflowY = _this$state3.menuOverflowY;
      var _this$props8 = this.props,
          dropdownStyle = _this$props8.dropdownStyle,
          dropdownClassName = _this$props8.dropdownClassName,
          containerStyle = _this$props8.containerStyle,
          containerClassName = _this$props8.containerClassName,
          rootStyle = _this$props8.rootStyle,
          rootClassName = _this$props8.rootClassName,
          customElementRenderer = _this$props8.customElementRenderer,
          menuStyle = _this$props8.menuStyle,
          menuClassName = _this$props8.menuClassName,
          valuesEquality = _this$props8.valuesEquality,
          readOnly = _this$props8.readOnly,
          onChange = _this$props8.onChange,
          inputMode = _this$props8.inputMode,
          children = _this$props8.children,
          appendToBody = _this$props8.appendToBody,
          multiple = _this$props8.multiple,
          multipleType = _this$props8.multipleType,
          disabled = _this$props8.disabled,
          size = _this$props8.size,
          variation = _this$props8.variation,
          status = _this$props8.status,
          icon = _this$props8.icon,
          classes = _this$props8.classes;
      var onSearch = readOnly ? undefined : this.props.onSearch;
      var focusedInput = inputFocused || isOpened;
      var multipleWithValue = multiple && Array.isArray(value) && value.length > 0;
      var options = multipleWithValue ? this.renderSelectedItems() : null;
      var canBeModified = !!onSearch || inputMode;
      var customElement = null;

      if (customElementRenderer) {
        if (!this.isValueEmpty(value) && !(isOpened && onSearch)) {
          var customElementRendered = customElementRenderer(value);
          var customElementRenderedClassName = customElementRendered.props.className;
          customElement = cloneElement(customElementRendered, {
            className: classnames(classes.custom, customElementRenderedClassName)
          });
        }
      } else if (multipleWithValue && (!isOpened || !onSearch)) {
        customElement = React.createElement(TagsInput, {
          className: classnames(classes.options, classes["options-".concat(multipleType)]),
          onChange: readOnly || !onChange ? undefined : this.changeValue,
          isExpanded: !isOpened || onSearch ? false : true,
          type: multipleType,
          disabled: disabled
        }, options);
      }

      var resultIsOpened = !readOnly && isOpened && (children.length > 0 || multiple && Array.isArray(value) && value.length > 0);
      var resultClassName = classnames(rootClassName, classes.root, (readOnly || !disabled && !canBeModified) && classes.isReadonly, classes[size], classes[variation], classes[status], disabled ? classes.isDisabled : classes.isEnabled, !readOnly && !disabled && canBeModified && classes.withSearch, icon && classes.withLeftIcon, this.showArrow && classes.withRightIcon, resultIsOpened && classes.isOpened, focusedInput && classes.isFocused, multiple && !onSearch && classes.isMultipleWithoutSearch);
      var dropdownResultClassName = classnames(dropdownClassName, classes.dropdown, multiple && classes.isMultipleDropdown);
      return React.createElement(OnClickOutside, {
        handler: this.closeOnClickOutside
      }, function (componentRef) {
        return React.createElement("div", {
          ref: componentRef,
          className: resultClassName,
          style: rootStyle
        }, React.createElement(Dropdown, {
          isOpened: resultIsOpened,
          anchor: function anchor(ref, _ref2) {
            var contentPointY = _ref2.contentPointY;
            return React.createElement("div", {
              className: classnames(containerClassName, (multiple || customElementRenderer) && classes.withCustom, contentPointY === 'top' ? classes.withBottomDropdown : classes.withTopDropdown),
              style: containerStyle,
              ref: ref
            }, _this3.renderInput(), customElement);
          },
          padding: false,
          style: dropdownStyle,
          className: dropdownResultClassName,
          overlayClassName: classes.dropdownContainer,
          appendToBody: appendToBody,
          anchorFullWidth: true,
          autoPositionY: !multiple,
          anchorPointY: "bottom",
          contentPointY: "top",
          closeOnClickOutside: false,
          cachePositionOptions: false,
          onOpen: _this3.onDropdownOpen,
          onClose: _this3.handleDropdownClose
        }, multipleWithValue && onSearch && React.createElement(TagsInput, {
          className: classnames(classes.selected, classes["options-".concat(multipleType)]),
          onChange: _this3.changeValue,
          isExpanded: true,
          onMouseDown: _this3.preventBlurInput,
          type: multipleType
        }, options), children.length > 0 && React.createElement(Menu, {
          style: menuStyle,
          className: classnames(menuClassName, classes.menu, classes["menuSize-".concat(size)], onSearch && multiple && classes.reducedHeight, menuOverflowY && classes["menuOverflowY-".concat(menuOverflowY)]),
          itemClassName: classes.menuItem,
          autoFocus: resultIsOpened && !inputFocused,
          value: multiple ? Array.isArray(value) ? value : emptyArr : value,
          valuesEquality: valuesEquality,
          onChange: _this3.changeValue,
          onEscKeyDown: _this3.closeOnEsc,
          onScroll: _this3.onMenuScroll,
          scrollRef: _this3.onMenuScrollMount,
          multiple: multiple,
          size: size
        }, children)));
      });
    }
  }, {
    key: "renderNativeSelect",
    value: function renderNativeSelect() {
      var _this4 = this;

      var _this$props9 = this.props,
          placeholder = _this$props9.placeholder,
          disabled = _this$props9.disabled,
          readOnly = _this$props9.readOnly,
          multiple = _this$props9.multiple,
          multipleType = _this$props9.multipleType,
          children = _this$props9.children,
          size = _this$props9.size,
          variation = _this$props9.variation,
          status = _this$props9.status,
          icon = _this$props9.icon,
          valuesEquality = _this$props9.valuesEquality,
          inputValueRenderer = _this$props9.inputValueRenderer,
          className = _this$props9.className,
          classes = _this$props9.classes,
          rootStyle = _this$props9.rootStyle,
          rootClassName = _this$props9.rootClassName;
      var _this$state4 = this.state,
          value = _this$state4.value,
          inputFocused = _this$state4.inputFocused;
      var resultValue = multiple ? [] : '';
      this.values = [];
      var options = [];
      Children.forEach(children, function (item, index) {
        var children = item.props.children;
        if (item.type.displayName !== 'RamblerUI(MenuItem)') throw new Error('Child component should be instance of <MenuItem />');
        if (typeof children !== 'string') throw new Error('Children of <MenuItem /> should be a string');
        options[index] = React.createElement("option", {
          key: children,
          value: index
        }, children);
        _this4.values[index] = item.props.value;

        if (multiple) {
          if (value.some(function (selectedItem) {
            return valuesEquality(selectedItem, item.props.value);
          })) resultValue.push(index);
        } else if (valuesEquality(value, item.props.value)) {
          resultValue = index;
        }
      });
      var selectedOptions = multiple && Array.isArray(value) && value.length > 0 && this.renderSelectedItems();
      var resultClassName = classnames(rootClassName, classes.root, classes.isNative, classes.withRightIcon, classes[size], classes[variation], classes[status], disabled ? classes.isDisabled : classes.isEnabled, multiple && [classes.isMultipleWithoutSearch, classes.withCustom], icon && classes.withLeftIcon, inputFocused && classes.isFocused);
      return React.createElement("div", {
        className: resultClassName,
        style: rootStyle
      }, React.createElement(Input, {
        className: classes.input,
        inputClassName: classnames(className, classes.field),
        disabled: disabled,
        onChange: noop,
        value: multiple ? '' : inputValueRenderer(value) || '',
        iconLeft: icon,
        iconRight: createElement(this.Arrow),
        iconRightClassName: classes.icon,
        activeBorderClassName: classes.inputBorder,
        tabIndex: -1,
        readOnly: true,
        placeholder: selectedOptions ? null : placeholder,
        isFocused: inputFocused
      }), selectedOptions && React.createElement(TagsInput, {
        className: classnames(classes.options, classes["options-".concat(multipleType)]),
        size: size,
        type: multipleType,
        disabled: disabled
      }, selectedOptions), React.createElement("select", _extends({}, this.getInputProps(), {
        className: classes.nativeSelect,
        multiple: multiple,
        value: resultValue,
        onChange: readOnly ? undefined : this.handleNativeSelectChange,
        onBlur: this.blurInput,
        onFocus: this.focusInput
      }), placeholder ? React.createElement("option", {
        disabled: true,
        value: ""
      }, placeholder) : multiple && multipleSelectFix, options));
    }
  }, {
    key: "render",
    value: function render() {
      return this.props.native && isNativeSelectAllowed && !this.props.onSearch ? this.renderNativeSelect() : this.renderSelect();
    }
  }, {
    key: "initialValue",
    get: function get() {
      var _this$props10 = this.props,
          multiple = _this$props10.multiple,
          value = _this$props10.value;
      return multiple ? Array.isArray(value) ? value : emptyArr : value;
    }
  }, {
    key: "showArrow",
    get: function get() {
      var _this$props11 = this.props,
          children = _this$props11.children,
          clearIcon = _this$props11.clearIcon,
          readOnly = _this$props11.readOnly;
      return !readOnly && children && children.length > 0 && !clearIcon;
    }
  }, {
    key: "showClearIcon",
    get: function get() {
      var _this$props12 = this.props,
          multiple = _this$props12.multiple,
          clearIcon = _this$props12.clearIcon;
      return !multiple && clearIcon && !this.isValueEmpty(this.state.value);
    }
  }], [{
    key: "getDerivedStateFromProps",
    value: function getDerivedStateFromProps(nextProps, prevState) {
      if (nextProps.value === prevState.value) return null;
      return Select.getValueState(nextProps.value, nextProps, prevState);
    }
  }, {
    key: "getValueState",
    value: function getValueState(value, nextProps, prevState) {
      var valuesEquality = nextProps.valuesEquality,
          multiple = nextProps.multiple;
      var oldValue = prevState.value;

      if (multiple) {
        var currValue = Array.isArray(oldValue) ? oldValue : emptyArr;
        var nextValue = Array.isArray(value) ? value : emptyArr;
        if (nextValue.length === currValue.length && nextValue.every(function (item, index) {
          return valuesEquality(item, currValue[index]);
        })) return null;
      } else if (valuesEquality(value, oldValue)) {
        return null;
      }

      return {
        value: value
      };
    }
  }]);

  return Select;
}(PureComponent);

Select.defaultProps = {
  multiple: false,
  multipleType: 'regular',
  clearIcon: false,
  status: null,
  size: 'medium',
  variation: 'awesome',
  disabled: false,
  readOnly: false,
  appendToBody: false,
  inputMode: false,
  valuesEquality: function valuesEquality(a, b) {
    return a === b;
  },
  inputValueRenderer: function inputValueRenderer(value) {
    return value;
  },
  children: []
};
Select.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Дополнительный CSS-класс поля
   */
  className: PropTypes.string,

  /**
   * Inline-стили поля
   */
  style: PropTypes.object,

  /**
   * Дополнительный CSS-класс `<Dropdown />`
   */
  dropdownClassName: PropTypes.string,

  /**
   * Inline-стили `<Dropdown />`
   */
  dropdownStyle: PropTypes.object,

  /**
   * Дополнительный CSS-класс `<Menu />`
   */
  menuClassName: PropTypes.string,

  /**
   * Inline-стили `<Menu />`
   */
  menuStyle: PropTypes.object,

  /**
   * Множественный выбор
   */
  multiple: PropTypes.bool,

  /**
   * Разновидность элементов-значений множественного выбора
   */
  multipleType: PropTypes.oneOf(['regular', 'background']),

  /**
   * Показывать кнопку очистки инпута
   */
  clearIcon: PropTypes.bool,

  /**
   * Дополнительный CSS-класс кнопки со стрелкой
   */
  arrowClassName: PropTypes.string,

  /**
   * Inline-стили кнопки со стрелкой
   */
  arrowStyle: PropTypes.object,

  /**
   * Выбранное значение, по-умолчанию считается, что это примитив. В случае множественного выбора - массив выбранных значений.
   */
  value: PropTypes.any,

  /**
   * Проверка равенства значений, задается, если
   * значением является объект. Ожидается, что возвращает `Boolean`
   */
  valuesEquality: PropTypes.func,

  /**
   * Функция рендера выбранного значения в поле, задается, если
   * значением является объект. Ожидается, что возвращает `String`
   */
  inputValueRenderer: PropTypes.func,

  /**
   * Функция рендера иконки при множественном выборе (`multiple=true`).
   * В качестве аргумента принимает `value`.
   * Ожидается, что возвращает `Element`. Возвращаемое значение передается через свойство `icon` в `TagsInputItem`
   */
  iconElementRenderer: PropTypes.func,

  /**
   * Плэйсхолдер
   */
  placeholder: PropTypes.string,

  /**
   * Светлый плейсхолдер, как в компоненте `<Input />`
   */
  lightPlaceholderColor: PropTypes.bool,

  /**
   * Доступность элемента
   */
  disabled: PropTypes.bool,

  /**
   * Доступность только для чтения
   */
  readOnly: PropTypes.bool,

  /**
   * Опции поля, массив элементов
   */
  children: PropTypes.arrayOf(PropTypes.element),

  /**
   * Иконка
   */
  icon: PropTypes.node,

  /**
   * Правая иконка (показывается вместо arrowIcon и clearIcon)
   */
  rightIcon: PropTypes.node,

  /**
   * Иконка стрелки
   */
  arrowIcon: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),

  /**
   * Размер
   */
  size: PropTypes.oneOf(['small', 'medium']),

  /**
   * Разновидность инпута
   */
  variation: PropTypes.oneOf(['regular', 'awesome', 'promo']),

  /**
   * Статусы валидации
   */
  status: PropTypes.oneOf(['error', 'warning', 'success', 'filled', null]),

  /**
   * Вставлять ли dropdown внутри body
   */
  appendToBody: PropTypes.bool,

  /**
   * Коллбек вызывающийся при фокусе
   */
  onFocus: PropTypes.func,

  /**
   * Коллбек вызывающийся при блюре
   */
  onBlur: PropTypes.func,

  /**
   * Коллбек вызывающийся при нажатии кнопки клавиатуры
   */
  onKeyDown: PropTypes.func,

  /**
   * Коллбек вызывающийся при изменении состояния
   */
  onChange: PropTypes.func,

  /**
   * Коллбек вызывающийся при изменении поискового запроса
   */
  onSearch: PropTypes.func,

  /**
   * Функция рендера кастомного элемента над input, получает `value` в качестве значения.
   * Должна возвращать `Element`.
   */
  customElementRenderer: PropTypes.func,

  /**
   * Дополнительный CSS-класс корневого контейнера
   */
  rootClassName: PropTypes.string,

  /**
   * Inline-стили корневого контейнера
   */
  rootStyle: PropTypes.object,

  /**
   * Дополнительный CSS-класс контейнера кастомного элемента и `<Input>`
   */
  containerClassName: PropTypes.string,

  /**
   * Inline-стили контейнера кастомного элемента и `<Input>`
   */
  containerStyle: PropTypes.object,

  /**
   * Позволяет вводить произвольное значение в поле, ожидается, что `value` типа `String`
   */
  inputMode: PropTypes.bool,

  /**
   * Применить логику выбора опций нативного select'a на мобильных устройствах.
   * При использовании `onSearch` - не применяется.
   * `<MenuItem>` в качестве `children` должен принимать элемент типа `String`
   */
  native: PropTypes.bool,

  /**
   * Коллбек для передачи ссылки на ноду элемента input
   */
  inputRef: PropTypes.func
} : {};
export default withStyles(styles, {
  name: 'Select'
})(Select);